import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as PlaybooksService from '../services/PlaybooksService';
import { Playbook } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const playbooksKeys = {
    all: ['playbooks'] as const,
    lists: () => [...playbooksKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...playbooksKeys.lists(), { filters }] as const,
    details: () => [...playbooksKeys.all, 'detail'] as const,
    detail: (id: string) => [...playbooksKeys.details(), id] as const,
};

// Hooks
export const usePlaybooksQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: playbooksKeys.list(filters),
        queryFn: () => PlaybooksService.fetchRecords(filters),
    });
};

export const usePlaybookQuery = (id: string) => {
    return useQuery({
        queryKey: playbooksKeys.detail(id),
        queryFn: () => PlaybooksService.fetch(id),
        enabled: !!id,
    });
};

export const useCreatePlaybookMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PlaybooksService.add,
        onSuccess: () => {
            notifySuccess('Playbook created');
            queryClient.invalidateQueries({ queryKey: playbooksKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdatePlaybookMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Playbook }) =>
            PlaybooksService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Playbook updated');
            queryClient.invalidateQueries({ queryKey: playbooksKeys.lists() });
            queryClient.invalidateQueries({ queryKey: playbooksKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeletePlaybookMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PlaybooksService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Playbook deleted');
            queryClient.invalidateQueries({ queryKey: playbooksKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const usePlaybooksFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
